import { useState, type ReactNode } from 'react';
import { CONFIG, TOP_TIER, BOTTOM_TIER, tierName } from '@h2h/shared';
import { Sheet } from '../ui/Sheet';
import { ChevronUp, ChevronDown, ChevronRight, Info, Minus, Users, X } from '../ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { cap } from '../../lib/format';
import { cn } from '../../lib/cn';

/**
 * A self-contained "How divisions work" affordance: a trigger plus the explainer
 * sheet, wired together with its own open state. Drop it on any surface that shows
 * tiers, divisions or promotion so the guide is always one tap away.
 *
 *  - `card`   — a full-width tappable row (used on the standings board).
 *  - `inline` — a compact pill (Info + label), for section headers/footers.
 *  - `icon`   — a compact text link for section headers (no icon bubble).
 */
export function DivisionsHelp({
  tierIndex,
  variant = 'inline',
  label = 'How divisions work',
  className
}: {
  /** The player's current tier; falls back to the lowest tier when unknown. */
  tierIndex: number | null | undefined;
  variant?: 'card' | 'inline' | 'icon';
  label?: string;
  className?: string;
}) {
  const { impact } = useHaptics();
  const [open, setOpen] = useState(false);
  const tier = tierIndex ?? BOTTOM_TIER;

  const onOpen = () => {
    impact('light');
    setOpen(true);
  };

  return (
    <>
      {variant === 'card' && (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'tap flex items-center gap-3 rounded-[16px] border border-black/6 bg-card p-[12px_14px] text-left shadow-tile',
            className
          )}
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-wash">
            <Info width={17} height={17} strokeWidth={2.2} className="text-accent-ink" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-extrabold tracking-snug text-ink">{label}</span>
            <span className="block text-[11.5px] font-medium text-ink2">A quick, plain-English guide to climbing</span>
          </span>
          <ChevronRight width={18} height={18} strokeWidth={2.4} className="shrink-0 text-ink3" />
        </button>
      )}

      {variant === 'inline' && (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'tap inline-flex items-center gap-1.5 rounded-full bg-fill px-[11px] py-[6px] text-[12px] font-bold text-ink2',
            className
          )}
        >
          <Info width={13} height={13} strokeWidth={2.4} className="text-ink3" />
          {label}
        </button>
      )}

      {variant === 'icon' && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={label}
          className={cn(
            'tap text-[12px] font-semibold text-ink2 active:opacity-70',
            className
          )}
        >
          {label}
        </button>
      )}

      <DivisionsHelpSheet open={open} onClose={() => setOpen(false)} tierIndex={tier} />
    </>
  );
}

/** Bar heights for the dark ladder hero — tallest = top tier. */
const TIER_BAR_HEIGHTS = [38, 60, 84, 108, 132, 156] as const;

const TIER_BAR_GRADIENTS = [
  'linear-gradient(180deg,#C98A5E,#9A5F36)',
  'linear-gradient(180deg,#E7ECF2,#AEB7C4)',
  'linear-gradient(180deg,#FCDE7C,#E3A82B)',
  'linear-gradient(180deg,#E6ECF5,#9FB2CC)',
  'linear-gradient(180deg,#A7E6F4,#52B6D6)',
  'linear-gradient(180deg,#B98BF0,#6E3AC4)'
] as const;

/**
 * "How divisions work" — visual explainer opened from the standings screen.
 * Layout matches the redesign mock; every number reads from CONFIG so the guide
 * stays in sync with what the engine settles.
 */
export function DivisionsHelpSheet({
  open,
  onClose,
  tierIndex
}: {
  open: boolean;
  onClose: () => void;
  tierIndex: number;
}) {
  const tier = Math.max(BOTTOM_TIER, Math.min(TOP_TIER, tierIndex));
  const here = cap(tierName(tier));
  const up = cap(tierName(Math.min(TOP_TIER, tier + 1)));
  const down = cap(tierName(Math.max(BOTTOM_TIER, tier - 1)));
  const atTop = tier >= TOP_TIER;
  const atBottom = tier <= BOTTOM_TIER;

  const promote = CONFIG.PROMOTE_COUNT;
  const relegate = CONFIG.RELEGATE_COUNT;
  const groupMax = CONFIG.DIVISION_SIZE;
  const tierCount = CONFIG.TIERS.length;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      ariaLabel="How divisions work"
      className="flex max-h-[88vh] flex-col bg-[#F4F4F7]"
    >
      <div className="native-scroll no-scrollbar flex-1 overflow-y-auto px-5 pb-2 pt-1">
        {/* header */}
        <div className="flex items-start justify-between gap-3 px-0.5 pt-1">
          <div className="min-w-0">
            <h2 className="text-[27px] font-bold leading-[1.05] tracking-[-0.03em] text-ink">
              How divisions work
            </h2>
            <p className="mt-[7px] text-[13.5px] font-medium leading-[1.45] text-[#8A8A92]">
              Climb the ladder by beating your group. Here&apos;s the whole idea in under a minute.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap mt-0.5 grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-[#E6E6EC]"
          >
            <X width={13} height={13} strokeWidth={3} className="text-[#6E6E78]" />
          </button>
        </div>

        {/* 1 · the ladder (hero) */}
        <div className="mt-4 overflow-hidden rounded-[22px] bg-gradient-to-b from-[#15151B] to-[#0C0C12] px-[18px] pb-[15px] pt-[17px] shadow-[0_18px_36px_-20px_rgba(0,0,0,0.55)]">
          <div className="mb-3.5 flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#7E7E8A]">
              {tierCount} tiers · climb to the top
            </span>
            {!atTop ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-accent">
                <ChevronUp width={11} height={11} strokeWidth={3.4} />
                Promote up
              </span>
            ) : (
              <span className="text-[11px] font-extrabold text-[#E8C25E]">Top tier</span>
            )}
          </div>
          <div className="flex items-end justify-between gap-1.5">
            {CONFIG.TIERS.map((name, i) => {
              const active = i === tier;
              const label = name.slice(0, 4);
              return (
                <div key={name} className="relative flex flex-1 flex-col items-center gap-1.5">
                  {active && (
                    <span className="absolute -top-[19px] left-1/2 -translate-x-1/2 text-[9.5px] font-black tracking-[0.02em] text-accent">
                      YOU
                    </span>
                  )}
                  <div
                    style={{
                      height: TIER_BAR_HEIGHTS[i] ?? TIER_BAR_HEIGHTS[0],
                      backgroundImage: TIER_BAR_GRADIENTS[i] ?? TIER_BAR_GRADIENTS[0]
                    }}
                    className={cn(
                      'w-full rounded-[7px]',
                      active && 'border-2 border-accent shadow-[0_0_22px_rgba(36,197,106,0.55)]'
                    )}
                  />
                  <span
                    className={cn(
                      'text-[8.5px] font-extrabold tracking-[0.02em]',
                      active ? 'font-black text-white' : 'text-[#9A9AA4]'
                    )}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 2 · the one rule */}
        <div className="mt-3.5 rounded-[22px] bg-white px-4 pb-[17px] pt-4 shadow-[0_8px_22px_-16px_rgba(20,20,50,0.3)]">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.11em] text-[#A0A0AA]">
            The one rule
          </div>
          <h3 className="mt-[5px] text-[17px] font-bold leading-[1.2] tracking-[-0.02em] text-ink">
            Where you finish decides your move
          </h3>
          <p className="mt-1.5 text-[12.5px] font-medium leading-[1.45] text-[#8A8A92]">
            Every round you join a group of up to{' '}
            <strong className="font-bold text-ink">{groupMax} players</strong> from your tier
            ({here} right now). When it ends, everyone&apos;s ranked by points.
          </p>

          <div className="mt-3.5 flex items-stretch gap-[13px]">
            {/* ranked column */}
            <div className="flex w-[62px] shrink-0 flex-col overflow-hidden rounded-[13px] border border-[#EBEBF0]">
              <div className="flex flex-col items-center gap-px bg-accent/[0.12] px-0 py-[7px] pb-1.5">
                <span className="text-[8px] font-extrabold tracking-[0.06em] text-accent-ink">1ST</span>
                <div className="flex flex-col gap-[3px] pt-[3px]">
                  <RankBar tone="promote" strength={1} />
                  <RankBar tone="promote" strength={0.55} />
                  <RankBar tone="promote" strength={0.4} />
                </div>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center gap-[3px] bg-[#F7F7FA] px-0 py-2">
                <RankBar tone="middle" />
                <RankBar tone="middle" />
                <span className="text-[13px] font-extrabold leading-none text-[#C2C2CC]">⋮</span>
                <RankBar tone="middle" />
                <RankBar tone="middle" />
              </div>
              <div className="flex flex-col items-center gap-px bg-danger/[0.12] px-0 py-[7px] pb-1.5">
                <div className="flex flex-col gap-[3px] pb-[3px]">
                  <RankBar tone="relegate" strength={0.4} />
                  <RankBar tone="relegate" strength={0.55} />
                  <RankBar tone="relegate" strength={1} />
                </div>
                <span className="text-[8px] font-extrabold tracking-[0.06em] text-[#C2403B]">
                  {groupMax}
                  {ordinalTag(groupMax)}
                </span>
              </div>
            </div>

            {/* outcomes */}
            <div className="flex flex-1 flex-col gap-2">
              <OutcomeCard
                tone="up"
                icon={<ChevronUp width={15} height={15} strokeWidth={3.4} className="text-white" />}
                title={`Top ${promote} — promoted`}
                subtitle={atTop ? 'Finish 1st to be crowned' : `Up to ${up}`}
              />
              <OutcomeCard
                tone="hold"
                icon={<Minus width={15} height={15} strokeWidth={3.4} className="text-[#7C7C86]" />}
                title="The middle — stay"
                subtitle={`Hold your tier · still ${here}`}
              />
              <OutcomeCard
                tone="down"
                icon={<ChevronDown width={15} height={15} strokeWidth={3.4} className="text-white" />}
                title={`Bottom ${relegate} — relegated`}
                subtitle={atBottom ? `Already ${here} — can't go lower` : `Down to ${down}`}
              />
            </div>
          </div>
        </div>

        {/* 3 · two quick facts */}
        <div className="mt-2.5 flex gap-2.5 pb-3.5">
          <QuickFact
            icon={<Users width={16} height={16} strokeWidth={2.2} className="text-[#5B6EE1]" />}
            iconBg="bg-[#EEF1FF]"
            title="Fresh group each round"
            body="New rivals from your tier, everyone from zero."
          />
          <QuickFact
            icon={<ResetIcon />}
            iconBg="bg-accent/[0.13]"
            title="One tier per round"
            body="Resets every round. Jump in anytime."
          />
        </div>
      </div>

      {/* sticky CTA */}
      <div className="shrink-0 bg-gradient-to-b from-[#F4F4F7]/0 via-[#F4F4F7]/80 to-[#F4F4F7] px-5 pb-4 pt-3">
        <button
          type="button"
          onClick={onClose}
          className="tap flex w-full items-center justify-center rounded-2xl bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] py-[17px] shadow-[0_16px_32px_-12px_rgba(36,197,106,0.6)]"
        >
          <span className="text-[16px] font-extrabold tracking-[-0.01em] text-white">Got it</span>
        </button>
      </div>
    </Sheet>
  );
}

function RankBar({
  tone,
  strength = 1
}: {
  tone: 'promote' | 'middle' | 'relegate';
  strength?: number;
}) {
  const bg =
    tone === 'promote'
      ? `rgba(36,197,106,${strength})`
      : tone === 'relegate'
        ? `rgba(210,84,79,${strength})`
        : '#DADAE2';
  return <div style={{ background: bg }} className="h-1 w-6 rounded-[2px]" />;
}

function OutcomeCard({
  tone,
  icon,
  title,
  subtitle
}: {
  tone: 'up' | 'hold' | 'down';
  icon: ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-1 items-center gap-[11px] rounded-[13px] px-3 py-2.5',
        tone === 'up' && 'border border-accent/22 bg-accent/[0.09]',
        tone === 'hold' && 'border border-[#EBEBF0] bg-[#F4F4F7]',
        tone === 'down' && 'border border-danger/20 bg-danger/[0.08]'
      )}
    >
      <span
        className={cn(
          'grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px]',
          tone === 'up' && 'bg-accent shadow-[0_6px_14px_-6px_rgba(36,197,106,0.7)]',
          tone === 'hold' && 'bg-[#D9D9E0]',
          tone === 'down' && 'bg-danger shadow-[0_6px_14px_-6px_rgba(210,84,79,0.7)]'
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div
          className={cn(
            'text-[13px] font-extrabold tracking-[-0.01em]',
            tone === 'up' && 'text-[#0A8A60]',
            tone === 'hold' && 'text-[#5C5C66]',
            tone === 'down' && 'text-[#C2403B]'
          )}
        >
          {title}
        </div>
        <div
          className={cn(
            'text-[11.5px] font-semibold',
            tone === 'up' && 'text-[#3FAE84]',
            tone === 'hold' && 'text-[#9A9AA4]',
            tone === 'down' && 'text-[#D08581]'
          )}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function QuickFact({
  icon,
  iconBg,
  title,
  body
}: {
  icon: ReactNode;
  iconBg: string;
  title: string;
  body: string;
}) {
  return (
    <div className="flex-1 rounded-[18px] bg-white px-3.5 py-[13px] shadow-[0_8px_22px_-16px_rgba(20,20,50,0.3)]">
      <div className={cn('mb-2 grid h-[30px] w-[30px] place-items-center rounded-[9px]', iconBg)}>
        {icon}
      </div>
      <div className="text-[13px] font-extrabold leading-[1.2] tracking-[-0.01em] text-ink">{title}</div>
      <div className="mt-1 text-[11.5px] font-medium leading-[1.4] text-[#8A8A92]">{body}</div>
    </div>
  );
}

/** Refresh-arrows glyph from the redesign (not in the shared icon set). */
function ResetIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden className="text-accent-ink">
      <path
        d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.5 15"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ordinalTag(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'TH';
  switch (n % 10) {
    case 1:
      return 'ST';
    case 2:
      return 'ND';
    case 3:
      return 'RD';
    default:
      return 'TH';
  }
}
